import { EntityStatusChip } from '../../../components/entity-detail/EntityStatusChip.js';
import type { ReactElement } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, App } from 'antd';
import { SendOutlined, CheckOutlined } from '@ant-design/icons';
import { useTranslation } from '@govard/i18n-core/react';
import { EntityHeader } from '../../../components/entity-detail/EntityHeader.js';
import { isTelegramTouchTask } from '../../../sales-health/touch-strategy-markers.js';
import type { TaskItem } from '../api/tasks.client.js';
import {
  useCompleteTelegramTouchMutation,
  useDeleteTaskMutation,
  useUpdateTaskMutation,
} from '../hooks/useTaskMutations.js';
import {
  getTaskPriorityLabel,
  getTaskStatusLabel,
  PRIORITY_TONE,
  STATUS_TONE,
} from '../lib/taskMeta.js';

export function TaskDetailHeader({
  task,
  onEdit,
  onNote,
  onHistory,
}: {
  task: TaskItem;
  onEdit: () => void;
  onNote: () => void;
  onHistory: () => void;
}): ReactElement {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const navigate = useNavigate();
  const update = useUpdateTaskMutation();
  const remove = useDeleteTaskMutation();
  const touch = useCompleteTelegramTouchMutation();
  const prefix = 'crm.features.features.tasks.pages.taskcardpage.';
  const error = update.error ?? remove.error;
  const open = task.status !== 'done' && task.status !== 'cancelled';
  return (
    <>
      <EntityHeader
        backTo="/tasks"
        backLabel={t(`${prefix}к_задачам`)}
        title={task.title}
        chips={[
          <EntityStatusChip key="status" tone={STATUS_TONE[task.status]}>
            {getTaskStatusLabel(task.status)}
          </EntityStatusChip>,
          <EntityStatusChip key="priority" tone={PRIORITY_TONE[task.priority]}>
            {getTaskPriorityLabel(task.priority)}
          </EntityStatusChip>,
        ]}
        primary={{
          key: 'complete',
          icon: <CheckOutlined aria-hidden />,
          label: t(`crm.features.task_detail.${task.status === 'done' ? 'reopen' : 'complete'}`),
          disabled: update.isPending,
          onClick: () =>
            update.mutate({
              id: task.id,
              input: { status: task.status === 'done' ? 'in_progress' : 'done' },
            }),
        }}
        secondary={[
          ...(isTelegramTouchTask(task.description) && open
            ? [
                {
                  key: 'touch',
                  testId: 'tg-touch-done-button',
                  icon: <SendOutlined />,
                  label: t(`${prefix}касание_сделано`),
                  disabled: touch.isPending,
                  onClick: () =>
                    touch.mutate(task.id, {
                      onSuccess: () => {
                        void message.success(t(`${prefix}касание_зафиксировано`));
                      },
                      onError: () => {
                        void message.error(t(`${prefix}не_удалось_зафиксировать_касание`));
                      },
                    }),
                },
              ]
            : []),
          { key: 'edit', label: t(`${prefix}редактировать`), onClick: onEdit },
        ]}
        overflow={[
          { key: 'note', label: t('crm.features.task_detail.add_note'), onClick: onNote },
          { key: 'history', label: t(`${prefix}история_задачи`), onClick: onHistory },
          {
            key: 'delete',
            label: t(`${prefix}удалить`),
            danger: true,
            disabled: remove.isPending,
            confirm: {
              title: t(`${prefix}удалить_задачу`),
              description: t(`${prefix}задача_пропадет_из_списков_заметки_по_ней_`),
              okText: t('crm.shared.delete'),
              cancelText: t('crm.common.cancel'),
            },
            onClick: () =>
              remove.mutateAsync(task.id).then(
                () => navigate('/tasks'),
                () => undefined,
              ),
          },
        ]}
      />
      {error ? (
        <Alert
          showIcon
          type="error"
          title={t(
            remove.error
              ? 'crm.features.features.tasks.pages.taskcardpage.не_удалось_удалить_задачу'
              : 'crm.features.task_detail.action_error',
          )}
          description={error.message}
        />
      ) : null}
    </>
  );
}
